/**
 * /main exec layout (quick-261104-ia3, John 2026-11-04): the always-visible top
 * band (ARR → churn → pipeline → monitors) and the department tab panels.
 * Every row is a 12-column grid using only spans 4 / 5 / 6 / 7 / 8 / 12, and
 * tiles in a row share one height, so the page lines up.
 */
import { useCallback, useMemo, useState, type ReactNode } from 'react';

import { SALES_EFFECTIVENESS_URL } from '../../lib/exec/links.js';
import { computeChanges, computeProblems, type ProblemItem } from '../../lib/exec/monitors.js';
import { todayIso } from '../../lib/exec/periods.js';

import {
  ArrBridgeTile,
  ArrMovementTile,
  ChurnedCustomersTile,
  ConcentrationTile,
  FinanceLinksTile,
  RetentionTile,
  ServicesForecastLinkTile,
  ServicesPipelineTile,
  ServicesWonTile,
  SfRenewalsTile,
  UpcomingRenewalsTile,
} from './DeptTiles.js';
import { ArrTrendTile, ChurnTile, FunnelTile, FunnelTrendTile, PotentialOppsDialog } from './ExecTopTiles.js';
import { GroupedTile } from './GroupedTile.js';
import { MARKETING_LINE_TILES, MARKETING_STACK_TILES, MarketingLinesTile, MarketingStackTile } from './MarketingTiles.js';
import { ChangesTile, ProblemsTile } from './MonitorTiles.js';
import { RilletRefreshButton } from './RilletRefreshButton.js';
import {
  AvgDealTile,
  BigDealsTile,
  CoverageTile,
  CycleBySizeTile,
  CycleTrendTile,
  LossReasonTile,
  NextYearPipelineTile,
  OppsCreatedTile,
  OutboundTile,
  PeriodCountTile,
  SlippageTile,
  StalledTile,
  WinRateTile,
} from './SalesTiles.js';

import type { ExecResponse, MainScorecardResponse } from '@saledash/shared';

const SPAN: Record<number, string> = {
  4: 'col-span-12 lg:col-span-4',
  5: 'col-span-12 lg:col-span-5',
  6: 'col-span-12 lg:col-span-6',
  7: 'col-span-12 lg:col-span-7',
  8: 'col-span-12 lg:col-span-8',
  12: 'col-span-12',
};

export function Row({ children }: { children: ReactNode }): React.JSX.Element {
  return <div className="grid grid-cols-12 gap-3">{children}</div>;
}
export function Cell({ span, children }: { span: 4 | 5 | 6 | 7 | 8 | 12; children: ReactNode }): React.JSX.Element {
  return <div className={`${SPAN[span] ?? 'col-span-12'} min-w-0`}>{children}</div>;
}
export function SectionHead({ label, right }: { label: string; right?: ReactNode }): React.JSX.Element {
  return (
    <div className="mb-2 mt-1 flex items-center gap-3">
      <h2 className="section-label shrink-0">{label}</h2>
      <span aria-hidden className="h-px flex-1 bg-gradient-to-r from-ink-200 to-transparent" />
      {right}
    </div>
  );
}

export type ExecTab = 'sales' | 'marketing' | 'cs' | 'services' | 'finance';

// ─── Top band ───────────────────────────────────────────────────────────────
export function ExecBand({
  exec,
  scorecard,
  loading,
  onOpenTab,
}: {
  exec: ExecResponse | undefined;
  scorecard: MainScorecardResponse | undefined;
  loading: boolean;
  onOpenTab: (t: ExecTab) => void;
}): React.JSX.Element {
  const today = todayIso();
  const funnel = exec?.panels.funnel?.payload;
  const avgDeal = exec?.avgDealSize ?? scorecard?.grouped['MAIN-12']?.total_value ?? null;
  const problems = useMemo(() => computeProblems(exec, scorecard, today), [exec, scorecard, today]);
  const changes = useMemo(() => computeChanges(exec), [exec]);
  const [auditOpen, setAuditOpen] = useState(false);
  const openAudit = useCallback(() => {
    setAuditOpen(true);
  }, []);
  const closeAudit = useCallback(() => {
    setAuditOpen(false);
  }, []);
  return (
    <div className="space-y-3" data-testid="exec-band">
      <SectionHead
        label="Revenue"
        right={
          <span className="flex flex-wrap items-center justify-end gap-2">
          <RilletRefreshButton />
          <a
            href={SALES_EFFECTIVENESS_URL}
            target="_blank"
            rel="noreferrer"
            className="shrink-0 rounded-full border border-ink-200 bg-white px-3 py-1 text-xs font-medium text-brand-600 hover:border-brand-300"
          >
            Sales effectiveness by rep ↗
          </a>
          </span>
        }
      />
      <Row>
        <Cell span={7}>
          <ArrTrendTile />
        </Cell>
        <Cell span={5}>
          <ChurnTile />
        </Cell>
      </Row>
      <SectionHead label="Pipeline" />
      <Row>
        <Cell span={5}>
          <FunnelTile funnel={funnel} avgDeal={avgDeal} loading={loading} onAuditPotential={openAudit} />
        </Cell>
        <Cell span={7}>
          <FunnelTrendTile funnel={funnel} avgDeal={avgDeal} loading={loading} />
        </Cell>
      </Row>
      <SectionHead label="Monitor" />
      <Row>
        <Cell span={6}>
          <ProblemsTile
            items={problems}
            loading={loading}
            onOpenTab={(t: NonNullable<ProblemItem['tab']>) => {
              onOpenTab(t);
            }}
            onOpenPotential={openAudit}
          />
        </Cell>
        <Cell span={6}>
          <ChangesTile items={changes} loading={loading} />
        </Cell>
      </Row>
      <PotentialOppsDialog open={auditOpen} onClose={closeAudit} funnel={funnel} avgDeal={avgDeal} />
    </div>
  );
}

// ─── Department tabs ────────────────────────────────────────────────────────
export function SalesPanel({ exec, scorecard }: { exec: ExecResponse | undefined; scorecard: MainScorecardResponse | undefined }): React.JSX.Element {
  const act = exec?.panels.activity?.payload;
  return (
    <div className="space-y-3">
      <SectionHead label="Speed" />
      <Row>
        <Cell span={6}>
          <CycleTrendTile scorecard={scorecard} cycle={exec?.panels.cycle?.payload} />
        </Cell>
        <Cell span={6}>
          <CycleBySizeTile cycle={exec?.panels.cycle?.payload} />
        </Cell>
      </Row>
      <SectionHead label="Activity" />
      <Row>
        <Cell span={8}>
          <OutboundTile activity={act} />
        </Cell>
        <Cell span={4}>
          <PeriodCountTile
            title="New meetings booked"
            info="New-business meetings (inbound and outbound) put on the calendar in each period. Switch between week, month and quarter."
            points={act?.meetingsBooked}
            since={act?.since}
            footer="Salesforce events, meeting type Inbound New / Outbound New"
            testId="sales-meetings"
            tipText="New-business meetings booked in this period."
          />
        </Cell>
      </Row>
      <Row>
        <Cell span={8}>
          <OppsCreatedTile activity={act} />
        </Cell>
        <Cell span={4}>
          <PeriodCountTile
            title="Opportunities advanced"
            info="How many times a deal moved forward to a later stage (or to won) in each period. More moves means deals are progressing, not sitting."
            points={act?.oppsAdvanced}
            since={act?.since}
            footer="Forward stage moves, product deals"
            testId="sales-advanced"
            tipText="Forward stage moves in this period."
            options={['week', 'month', 'quarter']}
          />
        </Cell>
      </Row>
      <SectionHead label="Pipeline" />
      <Row>
        <Cell span={5}>
          <CoverageTile scorecard={scorecard} />
        </Cell>
        <Cell span={7}>
          <NextYearPipelineTile coverage={exec?.panels.coverage?.payload} />
        </Cell>
      </Row>
      <Row>
        <Cell span={6}>
          <BigDealsTile scorecard={scorecard} funnel={exec?.panels.funnel?.payload} />
        </Cell>
        <Cell span={6}>
          <AvgDealTile scorecard={scorecard} winrate={exec?.panels.winrate?.payload} funnel={exec?.panels.funnel?.payload} />
        </Cell>
      </Row>
      <SectionHead label="Efficiency" />
      <Row>
        <Cell span={6}>
          <WinRateTile winrate={exec?.panels.winrate?.payload} />
        </Cell>
        <Cell span={6}>
          <StalledTile scorecard={scorecard} />
        </Cell>
      </Row>
      <Row>
        <Cell span={6}>
          <SlippageTile scorecard={scorecard} />
        </Cell>
        <Cell span={6}>
          <LossReasonTile scorecard={scorecard} winrate={exec?.panels.winrate?.payload} />
        </Cell>
      </Row>
    </div>
  );
}

/**
 * Marketing — one tab, every tile a trend over time, two per row. Monthly
 * stacks + quarterly lines come from exec panels.marketing; MAIN-33 / MAIN-49
 * were already trends and stay GroupedTiles. MAIN-50/51 (time in stage) are
 * replaced by "Days to win: marketing vs other"; MAIN-45 stays removed.
 */
type MarketingCell =
  | { kind: 'stack'; key: keyof typeof MARKETING_STACK_TILES }
  | { kind: 'lines'; key: keyof typeof MARKETING_LINE_TILES }
  | { kind: 'grouped'; id: string };
const MARKETING_ROWS: MarketingCell[][] = [
  [{ kind: 'stack', key: 'leadsByStatus' }, { kind: 'stack', key: 'leadsBySource' }],
  [{ kind: 'stack', key: 'leadsByIndustry' }, { kind: 'stack', key: 'disqualByReason' }],
  [{ kind: 'stack', key: 'workingByOwner' }, { kind: 'stack', key: 'pipelineByChannel' }],
  [{ kind: 'grouped', id: 'MAIN-33' }, { kind: 'grouped', id: 'MAIN-49' }],
  [{ kind: 'lines', key: 'leadToOppByChannel' }, { kind: 'lines', key: 'winRateByChannel' }],
  [{ kind: 'lines', key: 'cycleByChannel' }, { kind: 'lines', key: 'daysToWin' }],
];

export function MarketingPanel({
  exec,
  scorecard,
  loading = false,
}: {
  exec?: ExecResponse | undefined;
  scorecard: MainScorecardResponse | undefined;
  loading?: boolean;
}): React.JSX.Element {
  const marketing = exec?.panels.marketing?.payload;
  return (
    <div className="space-y-3">
      {MARKETING_ROWS.map((row, r) => (
        <Row key={`mkt-row-${String(r)}`}>
          {row.map((c) => (
            <Cell key={c.kind === 'grouped' ? c.id : c.key} span={6}>
              {c.kind === 'grouped' ? (
                <GroupedTile metricId={c.id} data={scorecard?.grouped[c.id]} />
              ) : c.kind === 'stack' ? (
                <MarketingStackTile spec={MARKETING_STACK_TILES[c.key]} marketing={marketing} loading={loading} />
              ) : (
                <MarketingLinesTile spec={MARKETING_LINE_TILES[c.key]} marketing={marketing} loading={loading} />
              )}
            </Cell>
          ))}
        </Row>
      ))}
    </div>
  );
}

/**
 * "In progress" banner over the CS, Services and Finance tabs (John 2026-11-04).
 * Icon + the words, never colour alone; role="status" so it is announced.
 */
export function InProgressBanner(): React.JSX.Element {
  return (
    <div
      role="status"
      data-testid="in-progress-banner"
      className="flex items-start gap-3 rounded-[14px] border-2 border-warn bg-warn-bg px-4 py-3 text-warn-text"
    >
      <svg aria-hidden viewBox="0 0 20 20" className="mt-0.5 h-5 w-5 shrink-0" fill="none" stroke="currentColor" strokeWidth="1.8">
        <path d="M10 2.5 18 17H2L10 2.5Z" strokeLinejoin="round" />
        <path d="M10 8v4M10 14.5v.01" strokeLinecap="round" />
      </svg>
      <p className="text-sm">
        <strong className="font-semibold">In progress</strong> — these views are still being built and checked; treat the
        numbers as directional.
      </p>
    </div>
  );
}

/** Team tab: per-rep detail lives on the Sales Effectiveness board (John 2026-11-04). */
export function TeamLinkPanel(): React.JSX.Element {
  return (
    <section
      aria-label="Sales effectiveness by rep"
      data-testid="team-link-card"
      className="rounded-[14px] border border-ink-100 bg-white p-6 shadow-[0_1px_2px_rgba(16,28,45,0.04)]"
    >
      <h3 className="text-base font-semibold text-ink-900">Sales effectiveness by rep</h3>
      <p className="mt-1 text-sm text-ink-600">
        The Sales Effectiveness board shows per-rep outbound activity, meetings booked and opportunities advanced.
      </p>
      <a
        href={SALES_EFFECTIVENESS_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="mt-4 inline-flex items-center gap-1 rounded-full border border-ink-200 bg-white px-4 py-1.5 text-sm font-medium text-brand-600 hover:border-brand-300"
      >
        Open the Sales Effectiveness board ↗
      </a>
    </section>
  );
}

export function CsPanel({ exec }: { exec: ExecResponse | undefined }): React.JSX.Element {
  // Churn itself sits in the top band; this tab is retention, renewals and concentration.
  return (
    <div className="space-y-3">
      <InProgressBanner />
      <SectionHead label="Customers" right={<RilletRefreshButton />} />
      <Row>
        <Cell span={6}>
          <RetentionTile />
        </Cell>
        <Cell span={6}>
          <SfRenewalsTile cs={exec?.panels.cs?.payload} />
        </Cell>
      </Row>
      <Row>
        <Cell span={4}>
          <UpcomingRenewalsTile />
        </Cell>
        <Cell span={4}>
          <ChurnedCustomersTile />
        </Cell>
        <Cell span={4}>
          <ConcentrationTile />
        </Cell>
      </Row>
    </div>
  );
}

export function ServicesPanel({ exec }: { exec: ExecResponse | undefined }): React.JSX.Element {
  const svc = exec?.panels.services?.payload;
  return (
    <div className="space-y-3">
      <InProgressBanner />
      <Row>
        <Cell span={4}>
          <ServicesWonTile services={svc} />
        </Cell>
        <Cell span={4}>
          <ServicesPipelineTile services={svc} />
        </Cell>
        <Cell span={4}>
          <ServicesForecastLinkTile />
        </Cell>
      </Row>
    </div>
  );
}

export function FinancePanel({ scorecard }: { scorecard: MainScorecardResponse | undefined }): React.JSX.Element {
  return (
    <div className="space-y-3">
      <InProgressBanner />
      <SectionHead label="Recurring revenue" right={<RilletRefreshButton />} />
      <Row>
        <Cell span={6}>
          <ArrBridgeTile />
        </Cell>
        <Cell span={6}>
          <ArrMovementTile />
        </Cell>
      </Row>
      <Row>
        <Cell span={6}>
          <GroupedTile metricId="MAIN-42" data={scorecard?.grouped['MAIN-42']} />
        </Cell>
        <Cell span={6}>
          <GroupedTile metricId="MAIN-43" data={scorecard?.grouped['MAIN-43']} />
        </Cell>
      </Row>
      <Row>
        <Cell span={8}>
          <GroupedTile metricId="MAIN-44" data={scorecard?.grouped['MAIN-44']} />
        </Cell>
        <Cell span={4}>
          <FinanceLinksTile />
        </Cell>
      </Row>
    </div>
  );
}
